import React, { Component } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import PageContent from "@my-dld/components/PageContent";
import Strings from "@my-dld/assets/strings";
import EjariPageTitle from "../../../component/EjariPageTitle";
import FormRow from "@my-dld/components/Form/FormRow";
import PickerInput from "@my-dld/components/Form/PickerInput";
import { IdExtractor } from "@my-dld/utils/LookupUtil";
import { Lookup, TenantDocumentLookup } from "@eres/ejari-api/Entity";
import FormAction from "@my-dld/components/Form/FormAction";
import FormSection from "@my-dld/components/Form/FormSection";
import { FormFieldProps } from "@my-dld/components/Form/FormField";
import { LocaleUtil, StringUtil } from "@eres/core";
import EjariApi from "@eres/ejari-api";
import { LoadableDataState, showErrorMessage } from "@my-dld/utils/UIUtils";
import TextInputField from "@my-dld/components/Form/TextInputField";
import { ManagementContractContext } from "@eres/ejari-core/contexts/ManagementContractContext";
import { TenantDocumentTransformer } from "@eres/ejari-api/Utils";
import Dropzone from "react-dropzone";
import DocumentInputField from "@my-dld/components/Form/DocumentInputField";
import { AttachmentInfo } from "@eres/ejari-api/Entity/tenant";
import { RenderManagementContractDocs } from "../../../component/ManagementContract/RenderManagementContractDocs";
import { MCTerminateRequest } from "@eres/ejari-api/Entity/management_contract";
import { getEjariActiveUserId, getEjariUserType } from "@10x/sso";
import moment from "moment";
import { EjariLookUp } from "@eres/ejari-api/constants";

interface Props extends RoutedViewProps {

}

interface LookupState {
	reasons?: Lookup[];
	documents?: TenantDocumentLookup[];
}

interface FormState {
	reason?: Lookup;
	reasonText?: string;
}

interface State extends LookupState, FormState, LoadableDataState {
}

class TerminateManagementContract extends Component<Props, State> {
	static contextType = ManagementContractContext;
	context!: React.ContextType<typeof ManagementContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			loadState: "loading",
			errorContainer: {},
		};
	}

	componentDidMount() {
		Promise.allSettled( [
			EjariApi.getLookup( EjariLookUp.MC_TERMINATION_REASONS, "", "" ),
			EjariApi.getLookup( "GetAttachments", "3", "", TenantDocumentTransformer )
		] ).then( ( [ reasons, documents ] ) => {
			if ( reasons.status === "fulfilled" && documents.status === "fulfilled" ) {
				this.setState( {
					reasons: reasons.value,
					documents: documents.value,
					loadState: "done"
				} );
			}
		} );
	}

	render() {
		const { reasons, reason, documents, errorContainer } = this.state;
		const isValid = this.isValid();
		const filedProps: Partial<FormFieldProps<any>> = {
			holder: this,
			containerClassName: "col-md-6 form-group",
		};
		return <PageContent
			title={ `${ Strings.terminate_management_contract }` }
			hideQLinks={ true }>
			<EjariPageTitle title={ Strings.terminate_management_contract }/>
			<React.Fragment>
				<FormSection title={ Strings.terminate_contract }>
					<FormRow { ...filedProps }>
						<PickerInput
							required={ true }
							isLoading={ reasons === undefined }
							id={ "reason" }
							holder={ this }
							toIdentifier={ IdExtractor }
							label={ Strings.termination_reason } items={ reasons }
						/>
						{ reason && reason.Id === -1 &&
							<TextInputField
								error={ this.state.errorContainer.reasonText }
								required={ true }
								label={ Strings.termination_reason }
								id={ "reasonText" }
								maxLength={ 400 }
							/>
						}
					</FormRow>
					<FormRow>
						{ documents && documents.map( ( docLookup: TenantDocumentLookup ) => {
							const isDocsAdded = ( docLookup && docLookup.Docs !== undefined && docLookup.Docs.length > 0 );
							const maxPages = docLookup.MaxPages ? docLookup.MaxPages : 1;
							const maxSizeKb = docLookup.MaxSizeKb ? ( docLookup.MaxSizeKb * 1000 ) : ( 2 * 1024 * 1000 );
							return <div key={ `doc_info_${ docLookup.Id }` } className="col-md-12 form-group">
								<Dropzone
									multiple={ maxPages > 1 }
									maxSize={ maxSizeKb }
									onDropRejected={ this.onDropRejected }
									onDrop={ ( files: File[] ) => {
										this.onDrop( docLookup, files );
									} }
									accept={ [ "image/png", "image/jpeg", "application/pdf" ] }>
									{ ( { getRootProps, getInputProps } ) => (
										<DocumentInputField
											{ ...getRootProps() }
											required={ true }
											dropZoneInputProps={ getInputProps() }
											label={ LocaleUtil.getLocalValue( docLookup ) }
											isDocsAdded={ isDocsAdded }
											error={ errorContainer[ docLookup.Id ] }
											disabled={ ( docLookup.Docs && docLookup.Docs.length === maxPages ) }/>
									) }
								</Dropzone>
								<span>
                                <small>{ Strings.tabu_upload_documents_note }</small><br/>
                                <small>{ Strings.max_num_files + " : " + maxPages }</small>
                            </span>
								<RenderManagementContractDocs docId={ docLookup.Id } documents={ docLookup.Docs } onDelete={ this.onDelete }/>
							</div>;
						} ) }
					</FormRow>
				</FormSection>
				<FormRow>
					<FormAction
						disablePrimaryAction={ !isValid }
						primaryActionLabel={ Strings.terminate_contract }
						onPrimaryAction={ this.onTerminate }/>
				</FormRow>
			</React.Fragment>
		</PageContent>;
	}

	onTerminate = () => {
		if ( !this.isValid() ) return;
		const today = moment( new Date() );
		const request: MCTerminateRequest = {
			ActiveUserID: getEjariActiveUserId(),
			ActiveUserType: getEjariUserType(),
			ContractId: this.context.id!,
			TerminationDate: today.toDate().asServerString(),
			McTerminationReasonId: this.state.reason?.Id!,
			TerminationReasonText: this.state.reasonText ?? "",
		};
		this.context.terminateManagementContract( this.context, request, this.state.documents! ).then( () => {
		} ).catch( () => {
		} );
	};

	isValid = () => {
		const { reason, reasonText, documents } = this.state;
		let isValid = true;
		documents?.forEach( doc => {
			if ( doc.IsMandatory && ( doc.Docs == null || doc.Docs.length === 0 ) ) isValid = false;
		} );
		isValid = isValid && reason != null;
		if ( isValid ) {
			if ( reason?.Id === -1 ) {
				isValid = isValid && !StringUtil.isEmptyStringOrNull( reasonText );
			}
		}
		return isValid;
	};

	private onDropRejected = () => {
		showErrorMessage( Strings.tabu_file_exceed_size );
	};

	private onDrop = ( selDoc: TenantDocumentLookup, files: File[] ) => {
		if ( selDoc.MaxPages && selDoc.MaxPages > 1 ) {
			if ( selDoc.Docs && selDoc.Docs.length > 0 ) {
				let maxAllowed = ( selDoc.MaxPages - selDoc.Docs.length );
				if ( files.length > maxAllowed )
					files = files.splice( 0, maxAllowed );
			} else if ( files.length > selDoc.MaxPages ) {
				files = files.splice( 0, selDoc.MaxPages );
			}
		}
		files.filter( x => x.type.startsWith( "image/" ) || x.type.startsWith( "application/pdf" ) )
		.forEach( file => this.addFile( selDoc, file ) );
	};

	private addFile( selDoc: TenantDocumentLookup, file: File ) {
		const reader = new FileReader();
		reader.onload = () => {
			const binaryStr = reader.result;
			if ( selDoc.Docs == null )
				selDoc.Docs = [];
			const newDoc: AttachmentInfo = {
				DocumentID: selDoc.Id,
				Type: file.type,
				LocalData: binaryStr as string,
				FileName: file.name,
			};
			if ( selDoc.MaxPages != null && selDoc.MaxPages > 1 ) {
				selDoc.Docs.push( newDoc );
			} else {
				selDoc.Docs = [ newDoc ];
			}
			const info = this.state.documents;
			let error = this.state.errorContainer;
			delete error[ selDoc.Id ];
			this.setState( {
				documents: info?.map( ( doc: TenantDocumentLookup ) => {
					return ( selDoc.Id === doc.Id ) ? selDoc : doc;
				} ) ?? [],
				errorContainer: error,
			} );
		};
		reader.readAsDataURL( file );
	}

	private onDelete = ( docId: number, index: number ) => {
		const { documents } = this.state;
		const d = documents?.map( d => d.Id === docId ? { ...d, Docs: d.Docs?.filter( ( _, i ) => i !== index ) } : d );
		this.setState( {
			documents: d,
		} );
	};
}

export default TerminateManagementContract;
